import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { SessionUser } from '@champdle/contracts';
import { ApiRequestError } from '../api.js';
import { useAccountActions } from '../queries.js';
import type { View } from '../route.js';

interface Props {
  user: SessionUser;
  go: (view: View) => void;
}

export function AccountPage({ user, go }: Props) {
  const { logout, deleteAccount } = useAccountActions();
  const id = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => headingRef.current?.focus(), []);

  const remove = (e: FormEvent): void => {
    e.preventDefault();
    if (!deleteAccount.isPending) deleteAccount.mutate(password, { onSuccess: () => go('play') });
  };
  const deleteError = deleteAccount.error;

  return (
    <section className="panel" aria-labelledby={`${id}-h`}>
      <h2 id={`${id}-h`} ref={headingRef} tabIndex={-1}>
        Your account
      </h2>
      <p>
        Signed in as <strong>{user.username}</strong>.
      </p>

      <div className="actions">
        <button
          type="button"
          disabled={logout.isPending}
          onClick={() => logout.mutate(undefined, { onSuccess: () => go('play') })}
        >
          Log out
        </button>
        <a className="button" href="/api/auth/export" download>
          Download my data
        </a>
        <button type="button" className="link" onClick={() => go('play')}>
          Back to the game
        </button>
      </div>
      {logout.isError ? (
        <p className="form__error" role="alert">
          Could not log out. Try again.
        </p>
      ) : null}

      <h3>Delete account</h3>
      <p className="muted">
        This permanently erases your account, games and stats. It cannot be undone.
      </p>
      {confirming ? (
        <form className="form" onSubmit={remove} noValidate>
          <label htmlFor={`${id}-pw`}>Confirm with your password</label>
          <input
            id={`${id}-pw`}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {deleteError ? (
            <p className="form__error" role="alert">
              {deleteError instanceof ApiRequestError
                ? deleteError.message
                : 'Something went wrong. Please try again.'}
            </p>
          ) : null}
          <div className="actions">
            <button type="submit" className="danger" disabled={deleteAccount.isPending}>
              Delete my account
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirming(false);
                setPassword('');
                deleteAccount.reset();
              }}
            >
              Keep my account
            </button>
          </div>
        </form>
      ) : (
        <div className="actions">
          <button type="button" className="danger" onClick={() => setConfirming(true)}>
            Delete account…
          </button>
        </div>
      )}
    </section>
  );
}
